import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../components/Head/SeoTags";
import { fetchDirectory } from "../../services/api";

// Icon and color mapping for collections
const COLLECTION_ICONS = [
  { icon: "bi-bookmark-star", bg: "#EFF6FF", accent: "#2563EB" },
  { icon: "bi-lightbulb", bg: "#FEF3C7", accent: "#F59E0B" },
  { icon: "bi-code-square", bg: "#D1FAE5", accent: "#10B981" },
  { icon: "bi-palette", bg: "#FCE7F3", accent: "#EC4899" },
  { icon: "bi-gear", bg: "#EDE9FE", accent: "#8B5CF6" },
  { icon: "bi-book", bg: "#DBEAFE", accent: "#3B82F6" },
  { icon: "bi-globe", bg: "#FEE2E2", accent: "#EF4444" },
  { icon: "bi-diagram-3", bg: "#CCFBF1", accent: "#14B8A6" },
];

// Generate avatar gradient from name
const getAvatarGradient = (name) => {
  const gradients = [
    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    "linear-gradient(135deg, #10B981 0%, #059669 100%)",
    "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)",
    "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
    "linear-gradient(135deg, #EC4899 0%, #BE185D 100%)",
    "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
  ];
  const index = name ? name.charCodeAt(0) % gradients.length : 0;
  return gradients[index];
};

// Get icon data for a collection based on index
const getIconData = (index) => {
  return COLLECTION_ICONS[index % COLLECTION_ICONS.length];
};

const DirectoryPage = () => {
  const { t } = useTranslation();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const LIMIT = 20;

  const APP_URL = process.env.REACT_APP_APP_URL || "https://my.onbookmarks.com";

  useEffect(() => {
    const loadCollections = async () => {
      try {
        setLoading(true);
        const response = await fetchDirectory(LIMIT, 0);
        if (response.status === "success" && response.content) {
          setCollections(response.content.collections || []);
          setHasMore(response.content.hasMore || false);
          setTotalCount(response.content.totalCount || 0);
        }
      } catch (err) {
        console.error("Failed to load directory:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadCollections();
  }, []);

  const loadMore = async () => {
    try {
      setLoadingMore(true);
      const newOffset = offset + LIMIT;
      const response = await fetchDirectory(LIMIT, newOffset);
      if (response.status === "success" && response.content) {
        setCollections((prev) => [
          ...prev,
          ...(response.content.collections || []),
        ]);
        setHasMore(response.content.hasMore || false);
        setOffset(newOffset);
      }
    } catch (err) {
      console.error("Failed to load more:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const getCollectionUrl = (collection) => {
    // Collection pages now live on the marketing site (prerendered, indexable),
    // not the app. Same-domain absolute URL: crawlable link + valid schema URL.
    return `https://onbookmarks.com/directory/${collection.uuid}/${collection.domain}`;
  };

  return (
    <div className="directory-page">
      <SeoTags
        title={t("directory.seo.title")}
        description={t("directory.seo.description")}
        keywords="bookmark collections, curated bookmarks, shared bookmarks, public collections, bookmark directory, link collections, resource lists, curated links, bookmark sharing, community bookmarks"
        ogType="website"
        twitterCard="summary_large_image"
      />

      {/* CollectionPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: t("directory.seo.title"),
            description: t("directory.seo.description"),
            url: "https://onbookmarks.com/directory",
            inLanguage: "en-US",
            isPartOf: {
              "@type": "WebSite",
              name: "onBookmarks",
              url: "https://onbookmarks.com",
            },
            publisher: {
              "@type": "Organization",
              name: "onBookmarks",
              url: "https://onbookmarks.com",
              logo: {
                "@type": "ImageObject",
                url: "https://onbookmarks.com/images/logo.png",
                width: 512,
                height: 512,
              },
            },
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: totalCount,
              itemListElement: collections.slice(0, 10).map((collection, index) => ({
                "@type": "ListItem",
                position: index + 1,
                item: {
                  "@type": "Collection",
                  name: collection.name,
                  description: collection.description || `A bookmark collection with ${collection.bookmarksCount || 0} curated links`,
                  url: getCollectionUrl(collection),
                },
              })),
            },
          }),
        }}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://onbookmarks.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Directory",
                item: "https://onbookmarks.com/directory",
              },
            ],
          }),
        }}
      />

      {/* Hero Section */}
      <section className="directory-hero">
        <div className="hero-badge">
          <i className="bi bi-clock-history"></i>
          {t("directory.hero.badge")}
        </div>
        <h1
          dangerouslySetInnerHTML={{
            __html: t("directory.hero.title"),
          }}
        />
        <p className="lead">{t("directory.hero.description")}</p>
      </section>

      {/* Collections Section */}
      <section className="collections-section">
        {loading ? (
          <div className="loading-state">
            <div className="spinner-border text-purple" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="error-state">
            <i className="bi bi-exclamation-triangle"></i>
            <h3>{t("directory.error.title")}</h3>
            <p>{error}</p>
          </div>
        ) : collections.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-collection"></i>
            <h3>{t("directory.empty.title")}</h3>
            <p>{t("directory.empty.description")}</p>
            <Link to={`${APP_URL}/register`} className="btn btn-purple btn-lg">
              {t("directory.cta.button")}
            </Link>
          </div>
        ) : (
          <>
            <div className="section-header">
              <span className="section-title">
                {t("directory.section.title")}
              </span>
            </div>

            <div className="collections-grid">
              {collections.map((collection, index) => {
                const iconData = getIconData(index);
                // Only use the wide featured layout when we actually have
                // preview bookmarks to fill it — otherwise its right half is
                // blank. (The directory API currently returns none.)
                const isFeatured =
                  index === 0 &&
                  collections.length > 2 &&
                  collection.previewBookmarks?.length > 0;

                if (isFeatured) {
                  return (
                    <a
                      key={collection.uuid}
                      href={getCollectionUrl(collection)}
                      className="collection-card featured"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ "--card-accent": "#FCD34D" }}
                    >
                      <div className="featured-content">
                        <div className="featured-badge">
                          <i className="bi bi-star-fill"></i>
                          {t("directory.card.featured")}
                        </div>
                        <div
                          className="card-icon"
                          style={{ "--icon-bg": "rgba(251, 191, 36, 0.2)" }}
                        >
                          <i className={`bi ${iconData.icon}`}></i>
                        </div>
                        <h3 className="card-title" style={{ marginTop: 16 }}>
                          {collection.name}
                        </h3>
                        {collection.description && (
                          <p className="card-description">
                            {collection.description}
                          </p>
                        )}
                        <div className="card-meta">
                          <div className="meta-left">
                            <div className="meta-item">
                              <i className="bi bi-bookmark"></i>
                              {collection.bookmarksCount || 0}{" "}
                              {t("directory.card.bookmarks")}
                            </div>
                          </div>
                          {collection.user?.firstname && (
                            <div className="author">
                              <div
                                className="author-avatar"
                                style={{
                                  background: getAvatarGradient(
                                    collection.user.firstname
                                  ),
                                }}
                              >
                                {collection.user.firstname.charAt(0).toUpperCase()}
                              </div>
                              <span className="author-name">
                                {collection.user.firstname}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      {collection.previewBookmarks?.length > 0 && (
                        <div className="featured-preview">
                          {collection.previewBookmarks.slice(0, 3).map((bm) => (
                            <div key={bm.uuid} className="preview-item">
                              <div className="preview-favicon">
                                <i className="bi bi-link-45deg"></i>
                              </div>
                              <span className="preview-title">{bm.title}</span>
                              <span className="preview-domain">
                                {new URL(bm.url).hostname.replace("www.", "")}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </a>
                  );
                }

                return (
                  <a
                    key={collection.uuid}
                    href={getCollectionUrl(collection)}
                    className="collection-card"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ "--card-accent": iconData.accent }}
                  >
                    <div className="card-header">
                      <div
                        className="card-icon"
                        style={{
                          "--icon-bg": iconData.bg,
                          "--icon-color": iconData.accent,
                        }}
                      >
                        <i className={`bi ${iconData.icon}`}></i>
                      </div>
                    </div>
                    <h3 className="card-title">{collection.name}</h3>
                    {collection.description && (
                      <p className="card-description">
                        {collection.description}
                      </p>
                    )}
                    <div className="card-meta">
                      <div className="meta-left">
                        <div className="meta-item">
                          <i className="bi bi-bookmark"></i>
                          {collection.bookmarksCount || 0}{" "}
                          {t("directory.card.bookmarks")}
                        </div>
                      </div>
                      {collection.user?.firstname && (
                        <div className="author">
                          <div
                            className="author-avatar"
                            style={{
                              background: getAvatarGradient(
                                collection.user.firstname
                              ),
                            }}
                          >
                            {collection.user.firstname.charAt(0).toUpperCase()}
                          </div>
                          <span className="author-name">
                            {collection.user.firstname}
                          </span>
                        </div>
                      )}
                    </div>
                  </a>
                );
              })}
            </div>

            {hasMore && (
              <div className="load-more-container">
                <button
                  className="btn btn-outline-secondary btn-lg px-5"
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {t("directory.loadMore.loading")}
                    </>
                  ) : (
                    t("directory.loadMore.button")
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>{t("directory.cta.title")}</h2>
          <p>{t("directory.cta.description")}</p>
          <Link
            to={`${APP_URL}/register`}
            className="btn btn-purple btn-lg px-5"
          >
            {t("directory.cta.button")}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default DirectoryPage;
